import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Snackbar, Tooltip, Tabs, Tab } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import WidgetsIcon from '@material-ui/icons/WidgetsOutlined';
import AccountTreeIcon from '@material-ui/icons/AccountTreeOutlined';
import ViewAgendaIcon from '@material-ui/icons/ViewAgendaOutlined';
import TouchAppIcon from '@material-ui/icons/TouchApp';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';
import CanvasResolver from '../../core/renderer/CanvasResolver';
import { getDescriptor } from '../../core/registry/ComponentRegistry';
import { updateCanvas, undoConfig, setCurrentScreen, setCurrentDevice, setCurrentView } from '../../api/searchExperienceSlice';
import WidgetPreview from '../../preview/WidgetPreview';
import PropertiesEditor from '../canvas/PropertiesEditor';
import { generateId } from '../canvas/CanvasBuilder/constants';
import { collectDescendants, createCanvasNode, duplicateNode, findNodeParent } from '../canvas/CanvasBuilder/canvasOperations';
import { placeCanvas, removeCanvasNode } from '../canvas/CanvasBuilder/automaticPlacement';
import SearchViewSelect from '../toolbar/SearchViewSelect';
import DeviceToolbar from '../../../integration-wizard/embedded/components/Layout/ConfigStepLayout/components/DeviceToolbar';
import BlockOutline from '../workspace/BlockOutline';
import BlockLibrary from '../workspace/BlockLibrary';
import PreviewDropSurface from '../workspace/PreviewDropSurface';
import { usePreviewZoom, PreviewZoomControls } from '../workspace/PreviewZoom';
import BlockDestination from '../workspace/BlockDestination';
import { componentLabel } from '../../core/registry/componentLabels';
import BlockActions from '../workspace/BlockActions';
import { blockLabel, childSlots, outlineRows } from '../workspace/blockPresentation';
import styles from '../workspace/Workspace.module.scss';

/** Visual workspace with switchable navigation and a contextual inspector. */
const LayoutEditor = ({ client, shopId, disabled = false, onInteraction, previewActions }) => {
  const t = useLocalizedMessage();
  const dispatch = useDispatch();
  const rootRef = useRef(null);
  const [width, setWidth] = useState(1200);
  const [height, setHeight] = useState(600);
  const previewRef = useRef(null);
  const [selection, setSelection] = useState(null);
  const [navigation, setNavigation] = useState('blocks');
  const [toolbox, setToolbox] = useState('components');
  const [expanded, setExpanded] = useState(false);
  const [addDestination, setAddDestination] = useState(null);
  const libraryRef = useRef(null);
  const [moving, setMoving] = useState(false);
  const [destination, setDestination] = useState(null);
  const [notice, setNotice] = useState('');
  const [addRequest, setAddRequest] = useState(null);
  const picking = addRequest?.mode === 'canvas';
  const { config, schema, currentView: view, currentScreen: screen, currentDevice: device } = useSelector(state => state.searchExperience);
  const zoom = usePreviewZoom(previewRef, device, expanded);
  const scope = `${shopId}:${view}:${screen}:${device}`;
  const canvas = useMemo(() => {
    const resolved = CanvasResolver.resolve(config, { view, screen, device });
    if (!resolved || !(schema?.layoutContracts?.createV2 || resolved.layoutContractVersion === 2)) return resolved;
    return { ...resolved, nodes: resolved.nodes.map(node => node.type === 'columns' && !node.editorCreatedSlots
      ? { ...node, editorCreatedSlots: [] } : node) };
  }, [config, schema, view, screen, device]);
  const nodes = canvas?.nodes || [];
  const selected = selection?.scope === scope ? nodes.find(node => node.id === selection.id) : null;
  const parent = selected ? findNodeParent(nodes, selected.id) : null;
  const siblingIndex = parent?.children.indexOf(selected?.id) ?? -1;
  const rows = outlineRows(canvas, t);
  const selectedRow = rows.find(row => row.node.id === selected?.id);
  const selectedPath = selectedRow ? [...selectedRow.parents, selected.id] : [];
  const defaultDestination = (nodeId, graph = nodes) => {
    const node = graph.find(item => item.id === nodeId);
    const slots = childSlots(node);
    if (slots.length) return { parentId: node.id, slotKey: slots[0][0] };
    const owner = node && findNodeParent(graph, node.id);
    if (owner) return { parentId: owner.parentId, slotKey: owner.slotKey };
    const root = graph.find(item => item.id === canvas?.root);
    const rootSlot = childSlots(root)[0];
    return rootSlot ? { parentId: root.id, slotKey: rootSlot[0] } : null;
  };
  const insertionTarget = nodes.some(node => node.id === addDestination?.parentId && Array.isArray(node.slots?.[addDestination.slotKey]))
    ? addDestination : defaultDestination(selected?.id);
  const select = (node, source = 'other', graph = nodes) => {
    if (node?.type === 'columns') return;
    setAddDestination(defaultDestination(node?.id, graph));
    setSelection(node ? { id: node.id, scope } : null);
    if (onInteraction) onInteraction({ type: 'selection', nodeId: node?.id, source, navigation });
  };
  const commit = (nextNodes, root = canvas?.root) => {
    if (disabled || !canvas || nextNodes === nodes) return false;
    dispatch(updateCanvas({ view, screen, device, canvas: { ...canvas, root, nodes: nextNodes } }));
    return true;
  };
  const moveNode = (nodeId, position) => {
    const next = placeCanvas(canvas, position, nodeId, generateId);
    if (commit(next.nodes, next.root)) {
      setNotice(t('experience.design.blockMoved'));
    }
  };
  const moveSibling = (direction) => {
    const target = parent?.children[siblingIndex + direction];
    if (target) moveNode(selected.id, { nodeId: target, position: direction < 0 ? 'before' : 'after' });
  };
  const openAdd = (parentId) => {
    setAddDestination(defaultDestination(parentId || selected?.id));
    setToolbox('components');
    if (onInteraction) onInteraction({ type: 'toolbox', value: 'components' });
    requestAnimationFrame(() => libraryRef.current?.querySelector('input')?.focus());
  };
  const addAtPosition = (type, position, source = 'click') => {
    const descriptor = getDescriptor(type);
    if (!descriptor || !position || type === 'columns') return;
    const node = createCanvasNode(type, generateId(type), descriptor);
    const nextCanvas = placeCanvas(canvas, position, node, generateId);
    const next = nextCanvas.nodes;
    if (commit(next, nextCanvas.root)) {
      select(node, 'other', next);
      if (source === 'drop' && onInteraction) onInteraction({ type: 'drop', operation: 'add', nodeId: node.id, componentType: type, ...findNodeParent(next, node.id) });
      setNotice(t('experience.design.blockAdded', { name: blockLabel(node, t, canvas.root) }));
      return true;
    }
    return false;
  };
  const requestAdd = type => {
    if (disabled || !canvas) return;
    setNotice('');
    setAddRequest({ type, mode: 'dialog' });
  };
  const confirmAdd = () => {
    if (!addRequest || !insertionTarget) return;
    if (addAtPosition(addRequest.type, { nodeId: insertionTarget.parentId, slotKey: insertionTarget.slotKey, position: 'inside' })) setAddRequest(null);
  };
  const placeByClick = position => {
    if (picking && addAtPosition(addRequest.type, position)) setAddRequest(null);
  };
  const dropNode = (position, payload) => {
    setAddRequest(null);
    if (payload.nodeId) {
      const nextCanvas = placeCanvas(canvas, position, payload.nodeId, generateId);
      const next = nextCanvas.nodes;
      if (commit(next, nextCanvas.root)) {
        select(next.find(node => node.id === payload.nodeId), 'other', next);
        if (onInteraction) onInteraction({ type: 'drop', operation: 'move', nodeId: payload.nodeId, ...findNodeParent(next, payload.nodeId) });
        setNotice(t('experience.design.blockMoved'));
      }
    } else if (payload.type) addAtPosition(payload.type, position, 'drop');
  };
  const duplicate = () => {
    const next = duplicateNode(nodes, canvas.root, selected.id, generateId);
    const copy = next.find(node => !nodes.some(existing => existing.id === node.id));
    if (commit(next)) { select(copy, 'other', next); setNotice(t('experience.design.blockDuplicated')); }
  };
  const remove = () => {
    if (commit(removeCanvasNode(nodes, canvas.root, selected.id))) {
      select(null);
      setNotice(t('experience.design.blockRemoved'));
    }
  };

  useEffect(() => {
    setSelection(null); setAddDestination(null); setMoving(false); setNotice(''); setAddRequest(null);
    if (previewRef.current) previewRef.current.scrollTop = 0;
  }, [scope]);
  useEffect(() => {
    const element = rootRef.current;
    if (!element) return undefined;
    const measure = () => {
      setWidth(element.getBoundingClientRect().width);
      setHeight(Math.max(400, window.innerHeight - element.getBoundingClientRect().top - 16));
    };
    measure();
    window.addEventListener('resize', measure);
    if (typeof ResizeObserver === 'undefined') return () => window.removeEventListener('resize', measure);
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => { observer.disconnect(); window.removeEventListener('resize', measure); };
  }, []);

  const scrollToSelection = () => {
    const stage = previewRef.current;
    if (!stage || !selected) return;
    const target = [...stage.querySelectorAll('[data-preview-node-id]')].find(element => element.dataset.previewNodeId === selected.id);
    if (!target) return;
    const bounds = stage.getBoundingClientRect();
    const block = target.getBoundingClientRect();
    if (block.top < bounds.top || block.top > bounds.bottom - 64) stage.scrollTop += block.top - bounds.top - 24;
  };
  useEffect(scrollToSelection, [selected?.id, canvas, expanded]);

  const preview = (interactive) => <WidgetPreview
    compact dragEnabled={interactive} inspect={interactive} onNodeSelect={node => select(node, 'preview')} selectedNodeId={selected?.id}
    canvas={canvas} theme={config?.search?.theme || config?.theme}
    viewType={view} screen={screen} device={device} mode="text"
  />;
  const layoutWidth = expanded ? window.innerWidth : width;
  const workArea = (
    <div data-editor-work-area style={expanded ? { height: '100%' } : width >= 640 && !(selected && width < 1056) ? { height } : undefined} className={`${styles.workArea} ${selected ? styles.withInspector : ''} ${layoutWidth < 1056 ? styles.compact : ''} ${layoutWidth < 640 ? styles.narrow : ''}`}>
      <aside className={styles.navPane} aria-label={t('experience.design.yourContent')}>
        <Tabs value={toolbox === 'components' ? 'components' : navigation} onChange={(_, value) => {
          const nextToolbox = value === 'components' ? 'components' : 'content';
          setToolbox(nextToolbox);
          if (onInteraction) onInteraction({ type: 'toolbox', value: nextToolbox });
          if (value !== 'components') {
            setNavigation(value);
            if (onInteraction) onInteraction({ type: 'navigation', value });
          }
        }} variant="standard" classes={{ flexContainer: styles.toolboxTabList, indicator: styles.toolboxIndicator }} className={styles.toolboxTabs} aria-label={t('experience.design.yourContent')}>
          <Tab className={styles.toolboxTab} data-editor-toolbox="components" value="components" icon={<WidgetsIcon />} aria-label={t('searchInterface.workspace.components')} title={t('searchInterface.workspace.components')} />
          <Tab className={styles.toolboxTab} data-editor-toolbox="content" data-editor-navigation="tree" value="tree" icon={<AccountTreeIcon />} aria-label={t('experience.design.tree')} title={t('experience.design.tree')} />
          <Tab className={styles.toolboxTab} data-editor-toolbox="blocks" value="blocks" icon={<ViewAgendaIcon />} aria-label={t('experience.design.blocks')} title={t('experience.design.blocks')} />
        </Tabs>
        <div hidden={toolbox !== 'components'} className={styles.toolboxPanel}>
          <div ref={libraryRef} className={styles.sidebarLibrary}><BlockLibrary draggable disabled={disabled || !canvas}
            onAdd={requestAdd} /></div>
        </div>
        <div hidden={toolbox !== 'content'} className={styles.toolboxPanel}>
        <BlockOutline canvas={canvas} selectedId={selected?.id} onSelect={node => select(node, 'navigation')} onAdd={openAdd} onMove={moveNode} disabled={disabled} simple={navigation === 'blocks'} />
        </div>
      </aside>
      <section className={styles.previewPane} tabIndex={0} onKeyDown={zoom.onKeyDown} onMouseDown={event => {
        if (!event.target.closest('button, input, textarea, select, [data-preview-node-id], [contenteditable]')) event.currentTarget.focus({ preventScroll: true });
      }} aria-label={t('experience.design.designPreview')}>
        <div data-editor-preview-controls>
          <DeviceToolbar compact disabled={disabled}
            leadingControls={<SearchViewSelect config={config} view={view} disabled={disabled} onChange={value => {
              if (disabled) return;
              dispatch(setCurrentView({ view: value }));
              const screens = Object.keys(config?.search?.views?.[value]?.screens || {});
              if (!screens.includes(screen) && screens.length) dispatch(setCurrentScreen({ screen: screens.includes('results') ? 'results' : screens[0] }));
            }} />}
            trailingControls={previewActions} currentDevice={device} currentScreen={screen}
            screens={Object.keys(config?.search?.views?.[view]?.screens || {}).map(value => ({
              value, label: t(({ results: 'experience.design.screenResults', empty: 'experience.design.screenEmpty', noResults: 'experience.design.screenNoResults' })[value] || value),
            }))}
            onDeviceChange={value => { if (!disabled) dispatch(setCurrentDevice({ device: value })); }}
            onScreenChange={value => { if (!disabled) dispatch(setCurrentScreen({ screen: value })); }} />
          {device === 'mobile' && canvas === config?.search?.views?.[view]?.screens?.[screen]?.canvas && <div className={styles.sharedLayoutHint}>{t('searchInterface.workspace.sharedMobileLayout')}</div>}
        </div>
        {picking && <div className={styles.placementHint} role="status">
          <span>{t('experience.design.placeHint', { name: componentLabel(addRequest.type, t) })}</span>
          <Button size="small" onClick={() => setAddRequest(null)}>{t('common.cancel')}</Button>
        </div>}
        {!expanded && <Tooltip title={t('experience.design.expandPreview')}><IconButton className={styles.expandPreview} size="small" onClick={() => setExpanded(true)} aria-label={t('experience.design.expandPreview')}><FullscreenIcon /></IconButton></Tooltip>}
        <PreviewDropSurface ref={previewRef} className={styles.previewStage} canvas={canvas} disabled={disabled} onDropNode={dropNode} picking={picking} onPlace={placeByClick}>
          <div className={styles.zoomFrame} style={{ width: zoom.width * zoom.scale, height: zoom.contentHeight * zoom.scale || undefined }}>
            <div ref={zoom.contentRef} className={styles.previewSurface} data-preview-scale={zoom.scale}
              style={{ width: zoom.width, transform: `scale(${zoom.scale})` }}>{preview(!disabled)}</div>
          </div>
        </PreviewDropSurface>
        <div className={styles.previewFooter}>
          <div className={styles.previewBreadcrumbs}>
          {selectedPath.length ? selectedPath.map((id, index) => <React.Fragment key={id}>
            {index > 0 && <ChevronRightIcon fontSize="small" />}
            <Button onClick={() => select(nodes.find(node => node.id === id))}>{blockLabel(nodes.find(node => node.id === id), t, canvas.root)}</Button>
          </React.Fragment>) : <span>{t('experience.design.previewHint')}</span>}
          </div>
          <PreviewZoomControls zoom={zoom} />
        </div>
      </section>
      {selected && <aside className={styles.inspectorPane} aria-label={t('experience.design.blockSettings')}>
        <div className={styles.inspectorBody}>
          <PropertiesEditor selectedNode={selected} view={view} screen={screen} device={device} onAdd={childSlots(selected).length ? () => openAdd(selected.id) : undefined} onClose={() => {
            const row = [...(previewRef.current?.closest('[data-editor-work-area]') || rootRef.current).querySelectorAll('[data-outline-node]')].find(element => element.dataset.outlineNode === selected.id);
            const button = row?.querySelector('button[draggable]');
            if (button) button.focus();
            select(null);
          }} client={client} shopId={shopId} disabled={disabled} />
        </div>
          <BlockActions disabled={disabled || selected.id === canvas.root} canMoveUp={siblingIndex > 0} canMoveDown={siblingIndex >= 0 && siblingIndex < (parent?.children.length || 0) - 1}
            onUp={() => moveSibling(-1)} onDown={() => moveSibling(1)} onDuplicate={duplicate} onDelete={remove}
            onMove={() => { setDestination(defaultDestination(parent?.parentId)); setMoving(true); }} />
      </aside>}
    </div>
  );
  return <div ref={rootRef} className={styles.workspace} data-testid="experience-layout-workspace" data-variant="visual" onKeyDownCapture={event => {
    if (picking && event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setAddRequest(null); }
  }}>
    {!expanded && workArea}
    <Dialog open={moving} onClose={() => setMoving(false)} fullWidth maxWidth="sm" aria-labelledby="experience-move-title">
      <DialogTitle id="experience-move-title">{t('experience.design.moveTo')}</DialogTitle>
      <DialogContent><BlockDestination canvas={canvas} value={destination} onChange={setDestination} exclude={selected ? [...collectDescendants(nodes, selected.id)] : []} disabled={disabled} /></DialogContent>
      <DialogActions><Button onClick={() => setMoving(false)}>{t('common.cancel')}</Button><Button color="primary" variant="contained" disabled={disabled || !destination} onClick={() => { moveNode(selected.id, { nodeId: destination.parentId, slotKey: destination.slotKey, position: 'inside' }); setMoving(false); }}>{t('experience.design.moveBlock')}</Button></DialogActions>
    </Dialog>
    <Dialog open={expanded} onClose={() => setExpanded(false)} TransitionProps={{ onEntered: scrollToSelection }} fullScreen aria-labelledby="experience-preview-title">
      <DialogTitle disableTypography className={styles.dialogHeading}><h2 id="experience-preview-title">{t('experience.design.designPreview')}</h2><IconButton onClick={() => setExpanded(false)} aria-label={t('experience.design.closePreview')}><CloseIcon /></IconButton></DialogTitle>
      <DialogContent className={styles.fullWorkspace}>{workArea}</DialogContent>
    </Dialog>
    <Dialog open={addRequest?.mode === 'dialog'} onClose={() => setAddRequest(null)} fullWidth maxWidth="xs"
      disableRestoreFocus={picking} TransitionProps={{ onExited: () => { if (picking && previewRef.current) previewRef.current.focus(); } }} aria-labelledby="experience-add-title">
      <DialogTitle id="experience-add-title">{t('experience.design.addComponent', { name: addRequest ? componentLabel(addRequest.type, t) : '' })}</DialogTitle>
      <DialogContent>
        <p className={styles.addHint}>{t('experience.design.addLocationHint')}</p>
        <BlockDestination canvas={canvas} value={insertionTarget} onChange={setAddDestination} disabled={disabled} />
        <Button fullWidth className={styles.pickLocation} startIcon={<TouchAppIcon />} disabled={disabled} onClick={() => setAddRequest(previous => ({ ...previous, mode: 'canvas' }))}>{t('experience.design.pickInPreview')}</Button>
      </DialogContent>
      <DialogActions><Button onClick={() => setAddRequest(null)}>{t('common.cancel')}</Button><Button color="primary" variant="contained" disabled={disabled || !insertionTarget} onClick={confirmAdd}>{t('experience.design.addBlock')}</Button></DialogActions>
    </Dialog>
    <Snackbar open={Boolean(notice)} autoHideDuration={5000} onClose={() => setNotice('')} message={notice}
      action={<Button color="inherit" size="small" disabled={disabled} onClick={() => { dispatch(undoConfig()); setNotice(''); }}>{t('experience.design.undo')}</Button>} />
  </div>;
};
export default LayoutEditor;
